import type { FilterFieldConfig, FilterValue } from '../../types';
import { SelectFilter } from './select-filter';
import { SearchFilter } from './search-filter';
import { MultiSelectFilter } from './multi-select-filter';

// ============================================================================
// Types
// ============================================================================

interface FilterFieldProps {
  /** Field configuration */
  config: FilterFieldConfig;
  /** Current value */
  value: FilterValue;
  /** Change handler */
  onChange: (value: FilterValue) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional className */
  className?: string;
  /** Hide the label */
  hideLabel?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Polymorphic filter field component.
 * Renders the appropriate field component based on the field type.
 *
 * @example
 * ```tsx
 * <FilterField
 *   config={fieldConfig}
 *   value={filters[fieldConfig.key]}
 *   onChange={(value) => setFilter(fieldConfig.key, value)}
 * />
 * ```
 */
export function FilterField({
  config,
  value,
  onChange,
  disabled = false,
  className,
  hideLabel = false,
}: FilterFieldProps) {
  switch (config.type) {
    case 'select':
      return (
        <SelectFilter
          config={config as FilterFieldConfig<string>}
          value={(value as string) ?? config.defaultValue}
          onChange={onChange}
          disabled={disabled}
          className={className}
          hideLabel={hideLabel}
        />
      );

    case 'search':
      return (
        <SearchFilter
          config={config as FilterFieldConfig<string>}
          value={(value as string) ?? config.defaultValue}
          onChange={onChange}
          disabled={disabled}
          className={className}
          hideLabel={hideLabel}
        />
      );

    case 'multi-select':
      return (
        <MultiSelectFilter
          config={config as FilterFieldConfig<string[]>}
          value={(value as string[]) ?? config.defaultValue ?? []}
          onChange={onChange}
          disabled={disabled}
          className={className}
          hideLabel={hideLabel}
        />
      );

    case 'checkbox':
      // TODO: Implement CheckboxFilter
      // eslint-disable-next-line no-console
      console.warn(`Filter type "${config.type}" is not yet implemented`);
      return null;

    case 'date-range':
      // TODO: Implement DateRangeFilter
      // eslint-disable-next-line no-console
      console.warn(`Filter type "${config.type}" is not yet implemented`);
      return null;

    case 'number-range':
      // TODO: Implement NumberRangeFilter
      // eslint-disable-next-line no-console
      console.warn(`Filter type "${config.type}" is not yet implemented`);
      return null;

    default: {
      const exhaustiveCheck: never = config.type;
      // eslint-disable-next-line no-console
      console.warn(`Unknown filter type: ${exhaustiveCheck}`);
      return null;
    }
  }
}
